import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Dices, Sparkles, Ticket } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { SoiDaoPlayRow } from '../components/soi-dao/SoiDaoPlaysPanel';
import { walletService } from '../services/walletService';
import { yikiService } from '../services/yikiService';
import { soiDaoService } from '../services/soiDaoService';
import { laoService } from '../services/laoService';
import { formatMoney } from '../utils/formatMoney';
import { YIKI_BET_TYPE_LABELS } from '../utils/yikiUtils';
import { LAO_BET_TYPE_LABELS } from '../utils/laoUtils';

const PAGE_SIZE = 10;
const TABS = [
  { id: 'lao', label: 'หวยลาว', icon: Crown },
  { id: 'lottery', label: 'ลอตเตอรี่', icon: Ticket },
  { id: 'yiki', label: 'หวยยี่กี', icon: Dices },
  { id: 'soi-dao', label: 'สอยดาว', icon: Sparkles },
];

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('th-TH');
}

function LotteryTicketRow({ ticket }) {
  const payout = Number(ticket.payout) || 0;
  const isSettled = Boolean(ticket.settled_at);
  const isWin = Boolean(ticket.period_win) || payout > 0;

  let statusText = 'รอผล';
  let statusClass = 'bg-amber-50 text-amber-700';
  let rowClass = 'border-gray-100 bg-gray-50';

  if (isSettled && isWin) {
    statusText = 'ถูกรางวัล';
    statusClass = 'bg-green-100 text-green-800';
    rowClass = 'border-green-300 bg-green-50';
  } else if (isSettled) {
    statusText = 'ไม่ถูก';
    statusClass = 'bg-gray-100 text-gray-600';
  }

  return (
    <li
      className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-sm ${rowClass}`}
    >
      <div className="min-w-0">
        <p className="font-mono text-lg font-bold text-brand-red">
          {ticket.ticket_number}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {[ticket.ticket_type, ticket.bet_type].filter(Boolean).join(' · ') ||
            'ลอตเตอรี่'}
          {' · '}
          {formatDateTime(ticket.created_at)}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <span
          className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}
        >
          {statusText}
        </span>
        {isWin && payout > 0 ? (
          <p className="mt-1 text-sm font-bold text-green-700">
            ได้รางวัล {formatMoney(payout)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function YikiBetRow({ bet }) {
  const payout = Number(bet.payout) || 0;
  const isWin = bet.status === 'won' || payout > 0;

  let statusText = 'รอผล';
  let statusClass = 'bg-amber-50 text-amber-700';
  let rowClass = 'border-gray-100 bg-gray-50';

  if (bet.status === 'won' || isWin) {
    statusText = 'ถูกรางวัล';
    statusClass = 'bg-green-100 text-green-800';
    rowClass = 'border-green-300 bg-green-50';
  } else if (bet.status === 'lost') {
    statusText = 'ไม่ถูก';
    statusClass = 'bg-gray-100 text-gray-600';
  }

  return (
    <li
      className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-sm ${rowClass}`}
    >
      <div className="min-w-0">
        <p className="font-mono text-lg font-bold text-brand-red">{bet.number}</p>
        <p className="mt-0.5 text-xs text-gray-500">
          {bet.roomName || bet.roomId} ·{' '}
          {YIKI_BET_TYPE_LABELS[bet.betType] || bet.betType} ·{' '}
          {formatMoney(bet.amount)}
          {bet.roundNumber ? ` · รอบ #${bet.roundNumber}` : ''}
        </p>
        <p className="mt-0.5 text-xs text-gray-400">
          {formatDateTime(bet.created_at)}
          {bet.result ? ` · ผล ${bet.result}` : ''}
        </p>
      </div>
      <div className="shrink-0 text-right">
        <span
          className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}
        >
          {statusText}
        </span>
        {isWin && payout > 0 ? (
          <p className="mt-1 text-sm font-bold text-green-700">
            ได้รางวัล {formatMoney(payout)}
          </p>
        ) : null}
      </div>
    </li>
  );
}

function ListPagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="mt-4 flex items-center justify-between gap-3">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
        className="rounded-lg border border-red-100 px-3 py-2 text-sm font-semibold text-brand-red transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        ก่อนหน้า
      </button>
      <p className="text-sm text-gray-600">
        หน้า {page} / {totalPages}
      </p>
      <button
        type="button"
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
        className="rounded-lg border border-red-100 px-3 py-2 text-sm font-semibold text-brand-red transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
      >
        ถัดไป
      </button>
    </div>
  );
}

const emptyMeta = { total: 0, winCount: 0, totalPages: 0 };

const TAB_CONFIG = {
  lao: {
    label: 'หวยลาว',
    empty: 'ยังไม่มีประวัติหวยลาว',
    fetch: (params) => laoService.getBets(params),
    winCount: (result) => result.winCount || 0,
  },
  lottery: {
    label: 'ลอตเตอรี่',
    empty: 'ยังไม่มีตั๋วลอตเตอรี่',
    fetch: (params) => walletService.getTickets(params),
    winCount: (result) => result.winCount || 0,
  },
  yiki: {
    label: 'หวยยี่กี',
    empty: 'ยังไม่มีประวัติยี่กี',
    fetch: (params) => yikiService.getBets(params),
    winCount: (result) => result.winCount || 0,
  },
  'soi-dao': {
    label: 'สอยดาว',
    empty: 'ยังไม่มีประวัติสอยดาว',
    fetch: (params) => soiDaoService.getPlays(params),
    winCount: (result) =>
      result.winCount ??
      (result.items || []).filter((item) => Number(item.payout) > 0).length,
  },
};

export function MyTicketsPage() {
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState('lao');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [metaByTab, setMetaByTab] = useState({
    lao: emptyMeta,
    lottery: emptyMeta,
    yiki: emptyMeta,
    'soi-dao': emptyMeta,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const setTabMeta = useCallback((tab, meta) => {
    setMetaByTab((current) => ({ ...current, [tab]: meta }));
  }, []);

  const loadTab = useCallback(
    (tab, nextPage) => {
      if (!isAuthenticated) {
        setItems([]);
        return Promise.resolve();
      }

      setIsLoading(true);
      setError('');

      const config = TAB_CONFIG[tab];
      return config
        .fetch({ page: nextPage, limit: PAGE_SIZE })
        .then((result) => {
          setItems(result.items || []);
          setTabMeta(tab, {
            total: result.total || 0,
            winCount: config.winCount(result),
            totalPages: result.totalPages || 0,
          });
        })
        .catch((err) => {
          const message = err.response?.data?.message;
          setError(
            Array.isArray(message)
              ? message[0]
              : message || 'โหลดประวัติไม่สำเร็จ',
          );
          setItems([]);
        })
        .finally(() => setIsLoading(false));
    },
    [isAuthenticated, setTabMeta],
  );

  const loadOtherSummaries = useCallback(
    (tab) => {
      if (!isAuthenticated) return Promise.resolve();

      const others = Object.keys(TAB_CONFIG).filter((id) => id !== tab);
      return Promise.all(
        others.map((other) => {
          const config = TAB_CONFIG[other];
          return config
            .fetch({ page: 1, limit: 1 })
            .then((result) => {
              setTabMeta(other, {
                total: result.total || 0,
                winCount: config.winCount(result),
                totalPages: result.totalPages || 0,
              });
            })
            .catch(() => {});
        }),
      );
    },
    [isAuthenticated, setTabMeta],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setItems([]);
      setMetaByTab({
        lao: emptyMeta,
        lottery: emptyMeta,
        yiki: emptyMeta,
        'soi-dao': emptyMeta,
      });
      return;
    }
    loadTab(activeTab, page);
  }, [isAuthenticated, activeTab, page, loadTab]);

  useEffect(() => {
    if (!isAuthenticated) return;
    loadOtherSummaries(activeTab);
  }, [isAuthenticated, activeTab, loadOtherSummaries]);

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);
    setPage(1);
  };

  const handleRefresh = () => {
    loadTab(activeTab, page);
    loadOtherSummaries(activeTab);
  };

  if (!isAuthenticated) {
    return (
      <div className="mx-auto max-w-xl space-y-6 text-center">
        <h2 className="text-2xl font-bold text-brand-red sm:text-3xl">
          ตั๋วของฉัน
        </h2>
        <Card>
          <CardContent className="space-y-4 py-10">
            <p className="text-gray-600">กรุณาเข้าสู่ระบบเพื่อดูตั๋วทั้งหมด</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-red bg-brand-red px-6 py-3 text-lg font-semibold text-white transition-colors hover:bg-brand-red-dark"
            >
              เข้าสู่ระบบ
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeMeta = metaByTab[activeTab] || emptyMeta;
  const activeConfig = TAB_CONFIG[activeTab];
  const ActiveIcon =
    TABS.find((tab) => tab.id === activeTab)?.icon || Ticket;

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-brand-red sm:text-3xl">
          ตั๋วของฉัน
        </h2>
        <p className="mt-2 text-gray-600">
          ประวัติการแทงหวยลาว ลอตเตอรี่ หวยยี่กี และสอยดาว
        </p>
        <button
          type="button"
          onClick={handleRefresh}
          className="mt-3 text-sm font-semibold text-brand-red hover:underline"
        >
          รีเฟรช
        </button>
      </div>

      {error ? (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-center text-sm text-brand-red">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {TABS.map(({ id, label }) => {
          const meta = metaByTab[id] || emptyMeta;
          return (
            <div
              key={id}
              className="rounded-xl border border-red-100 bg-white px-4 py-3 text-center"
            >
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-brand-red">{meta.total}</p>
              <p className="text-xs text-green-700">ถูก {meta.winCount}</p>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {TABS.map(({ id, label, icon: Icon }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => handleTabChange(id)}
              className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors ${
                isActive
                  ? 'bg-brand-red text-white'
                  : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-red-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ActiveIcon className="h-5 w-5" />
            {activeConfig.label}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && items.length === 0 ? (
            <p className="text-sm text-gray-500">กำลังโหลด...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500">{activeConfig.empty}</p>
          ) : (
            <>
              <ul className="space-y-2">
                {activeTab === 'lao'
                  ? items.map((bet) => (
                      <YikiBetRow
                        key={`lao-${bet.id}`}
                        bet={{
                          ...bet,
                          roomName: bet.periodKey,
                          betType: bet.betTypeLabel || LAO_BET_TYPE_LABELS[bet.betType] || bet.betType,
                        }}
                      />
                    ))
                  : null}
                {activeTab === 'lottery'
                  ? items.map((ticket) => (
                      <LotteryTicketRow
                        key={`lotto-${ticket.id}`}
                        ticket={ticket}
                      />
                    ))
                  : null}
                {activeTab === 'yiki'
                  ? items.map((bet) => (
                      <YikiBetRow key={`yiki-${bet.id}`} bet={bet} />
                    ))
                  : null}
                {activeTab === 'soi-dao'
                  ? items.map((play) => (
                      <SoiDaoPlayRow key={`soi-${play.id}`} play={play} />
                    ))
                  : null}
              </ul>
              <ListPagination
                page={page}
                totalPages={activeMeta.totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
